import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import { ViewType, SessionMode, Participant, ClipboardMessage, WebSocketMessage, ContextMenuData } from '../types';
import { useAlert } from '../hooks/useAlert';
import { WebRTCService } from '../services/WebRTCService';
import { ClipboardService } from '../services/ClipboardService';
import { WebSocketService } from '../services/WebSocketService';

interface ClipShareState {
  currentView: ViewType;
  sessionMode: SessionMode;
  sessionCode: string | null;
  userNickname: string;
  userId: string;
  isHost: boolean;
  participants: Map<string, Participant>;
  messages: ClipboardMessage[];
  clipboardMonitorActive: boolean;
  chatMode: 'auto' | 'manual'; // auto = sledování schránky, manual = chat
  contextMenu: ContextMenuData;
  connectionStatus: 'connected' | 'disconnected' | 'connecting';
}

type ClipShareAction = 
  | { type: 'SET_VIEW'; payload: ViewType }
  | { type: 'SET_SESSION_MODE'; payload: SessionMode }
  | { type: 'SET_SESSION_CODE'; payload: string | null }
  | { type: 'SET_USER_NICKNAME'; payload: string }
  | { type: 'SET_IS_HOST'; payload: boolean }
  | { type: 'SET_PARTICIPANTS'; payload: Map<string, Participant> }
  | { type: 'ADD_MESSAGE'; payload: ClipboardMessage }
  | { type: 'CLEAR_MESSAGES' }
  | { type: 'TOGGLE_CLIPBOARD_MONITOR' }
  | { type: 'SET_CHAT_MODE'; payload: 'auto' | 'manual' }
  | { type: 'SET_CONTEXT_MENU'; payload: ContextMenuData }
  | { type: 'HIDE_CONTEXT_MENU' }
  | { type: 'SET_CONNECTION_STATUS'; payload: 'connected' | 'disconnected' | 'connecting' }
  | { type: 'RESET_SESSION' };

const initialState: ClipShareState = {
  currentView: 'main',
  sessionMode: 'host',
  sessionCode: null,
  userNickname: '',
  userId: '',
  isHost: false,
  participants: new Map(),
  messages: [],
  clipboardMonitorActive: true,
  chatMode: 'auto',
  contextMenu: {
    type: 'text',
    content: '',
    x: 0,
    y: 0,
    visible: false
  },
  connectionStatus: 'disconnected'
};

const clipShareReducer = (state: ClipShareState, action: ClipShareAction): ClipShareState => {
  switch (action.type) {
    case 'SET_VIEW':
      return { ...state, currentView: action.payload };
    case 'SET_SESSION_MODE':
      return { ...state, sessionMode: action.payload };
    case 'SET_SESSION_CODE':
      return { ...state, sessionCode: action.payload };
    case 'SET_USER_NICKNAME':
      return { ...state, userNickname: action.payload };
    case 'SET_IS_HOST':
      return { ...state, isHost: action.payload };
    case 'SET_PARTICIPANTS':
      return { ...state, participants: action.payload };
    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.payload] };
    case 'CLEAR_MESSAGES':
      return { ...state, messages: [] };
    case 'TOGGLE_CLIPBOARD_MONITOR':
      return { ...state, clipboardMonitorActive: !state.clipboardMonitorActive };
    case 'SET_CHAT_MODE':
      return { ...state, chatMode: action.payload };
    case 'SET_CONTEXT_MENU':
      return { ...state, contextMenu: action.payload };
    case 'HIDE_CONTEXT_MENU':
      return { ...state, contextMenu: { ...state.contextMenu, visible: false } };
    case 'SET_CONNECTION_STATUS':
      return { ...state, connectionStatus: action.payload };
    case 'RESET_SESSION':
      return {
        ...state,
        sessionCode: null,
        isHost: false,
        participants: new Map(),
        messages: [],
        currentView: 'main'
      };
    default:
      return state;
  }
};

interface ClipShareContextType {
  state: ClipShareState;
  dispatch: React.Dispatch<ClipShareAction>;
  createSession: () => Promise<void>;
  joinSession: (code: string) => Promise<void>;
  leaveSession: () => void;
  sendMessage: (message: string) => void;
  sendClipboardContent: (data: { type: 'text' | 'image'; content: string }) => void;
  copyToClipboard: (data: { type: 'text' | 'image'; content: string }) => Promise<void>;
  showContextMenu: (event: React.MouseEvent, data: { type: 'text' | 'image'; content: string }) => void;
  hideContextMenu: () => void;
  generateUserId: () => string;
  generateDefaultNickname: () => string;
}

const ClipShareContext = createContext<ClipShareContextType | null>(null);

export const ClipShareProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(clipShareReducer, {
    ...initialState,
    userId: generateUserId(),
    userNickname: generateDefaultNickname()
  });
  
  const { showAlert } = useAlert();
  const webRTCService = useRef<WebRTCService>();
  const clipboardService = useRef<ClipboardService>();
  const webSocketService = useRef<WebSocketService>();

  useEffect(() => {
    // Initialize services
    webRTCService.current = new WebRTCService();
    clipboardService.current = new ClipboardService();
    webSocketService.current = new WebSocketService();

    // Setup WebSocket message handler
    webSocketService.current.onMessage = handleWebSocketMessage;
    webSocketService.current.onConnectionChange = (status) => {
      dispatch({ type: 'SET_CONNECTION_STATUS', payload: status });
    };

    // Setup WebRTC message handler
    webRTCService.current.onMessage = handlePeerMessage;
    webRTCService.current.setWebSocketService(webSocketService.current);

    // Setup clipboard monitor
    clipboardService.current.onClipboardChange = (data) => {
      if (state.clipboardMonitorActive && state.sessionCode && state.chatMode === 'auto') {
        sendClipboardContent(data);
      }
    };

    return () => {
      webSocketService.current?.disconnect();
      clipboardService.current?.stopMonitoring();
      webRTCService.current?.cleanup();
    };
  }, []);

  // Start/stop clipboard monitoring based on state
  useEffect(() => {
    if (state.clipboardMonitorActive && state.chatMode === 'auto' && state.sessionCode) {
      clipboardService.current?.startMonitoring();
    } else {
      clipboardService.current?.stopMonitoring();
    }
  }, [state.clipboardMonitorActive, state.chatMode, state.sessionCode]);

  function generateUserId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  function generateDefaultNickname(): string {
    const adjectives = ['Rychlý', 'Chytrý', 'Tajný', 'Super', 'Mega', 'Cool', 'Pro'];
    const nouns = ['Panda', 'Robot', 'Ninja', 'Pilot', 'Hacker', 'Guru', 'Master'];
    const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
    const noun = nouns[Math.floor(Math.random() * nouns.length)];
    return `${adj} ${noun}`;
  }

  const handleWebSocketMessage = async (data: WebSocketMessage) => {
    switch (data.type) {
      case 'session_created':
        dispatch({ type: 'SET_SESSION_CODE', payload: data.code });
        dispatch({ type: 'SET_IS_HOST', payload: true });
        webRTCService.current?.setSessionCode(data.code);
        showAlert('Kód relace byl vytvořen!', 'success');
        break;

      case 'session_joined':
        dispatch({ type: 'SET_SESSION_CODE', payload: data.code });
        dispatch({ type: 'SET_IS_HOST', payload: false });
        dispatch({ type: 'SET_VIEW', payload: 'chat' });
        webRTCService.current?.setSessionCode(data.code);
        showAlert('Připojeno k relaci!', 'success');
        break;

      case 'session_error':
        showAlert(data.message, 'error');
        break;

      case 'peer_joined':
        await webRTCService.current?.handlePeerJoined(data.peerId);
        updateParticipants(data.participants);
        
        if (state.isHost && state.currentView !== 'chat') {
          dispatch({ type: 'SET_VIEW', payload: 'chat' });
          showAlert(`${data.participants.find((p: Participant) => p.id === data.peerId)?.nickname || 'Někdo'} se připojil! Chat spuštěn.`, 'success');
        }
        break;

      case 'peer_left':
        webRTCService.current?.handlePeerLeft(data.peerId);
        updateParticipants(data.participants);
        
        if (state.isHost && data.participants.length === 1) {
          showAlert('Všichni účastníci odešli. Čekání na nové připojení...', 'warning');
        }
        break;

      case 'webrtc_offer':
        await webRTCService.current?.handleOffer(data.from, data.offer);
        break;

      case 'webrtc_answer':
        await webRTCService.current?.handleAnswer(data.from, data.answer);
        break;

      case 'webrtc_ice':
        await webRTCService.current?.handleIceCandidate(data.from, data.candidate);
        break;

      case 'session_ended':
        showAlert('Relace byla ukončena', 'warning');
        leaveSession();
        break;
    }
  };

  const handlePeerMessage = (data: any, fromPeerId: string) => {
    const participant = state.participants.get(fromPeerId);
    const participantName = participant?.nickname || `Uživatel ${fromPeerId.slice(0, 4)}`;

    const message: ClipboardMessage = {
      type: data.type,
      content: data.content,
      timestamp: Date.now(),
      fromPeerId,
      participantName
    };

    dispatch({ type: 'ADD_MESSAGE', payload: message });
  };

  const updateParticipants = (participants: Participant[]) => {
    const participantMap = new Map<string, Participant>();
    participants.forEach(p => participantMap.set(p.id, p));
    dispatch({ type: 'SET_PARTICIPANTS', payload: participantMap });
  };

  const createSession = async () => {
    if (!webSocketService.current?.isConnected()) {
      showAlert('Spojení se serverem není aktivní', 'error');
      return;
    }

    await webSocketService.current.send({
      type: 'create_session',
      userId: state.userId,
      nickname: state.userNickname
    });
  };

  const joinSession = async (code: string) => {
    if (code.length !== 6) {
      showAlert('Zadejte platný 6místný kód', 'error');
      return;
    }

    if (!webSocketService.current?.isConnected()) {
      showAlert('Spojení se serverem není aktivní', 'error');
      return;
    }

    await webSocketService.current.send({
      type: 'join_session',
      code: code.toUpperCase(),
      userId: state.userId,
      nickname: state.userNickname
    });
  };

  const leaveSession = () => {
    webRTCService.current?.cleanup();
    clipboardService.current?.stopMonitoring();
    
    if (webSocketService.current?.isConnected() && state.sessionCode) {
      webSocketService.current.send({
        type: 'leave_session',
        sessionCode: state.sessionCode,
        userId: state.userId
      });
    }

    dispatch({ type: 'RESET_SESSION' });
    showAlert('Relace byla ukončena', 'warning');
  };

  const sendMessage = (message: string) => {
    if (!message.trim()) return;
    
    const data = { type: 'text' as const, content: message.trim() };
    webRTCService.current?.broadcast(data);
    
    // Add to local messages
    const localMessage: ClipboardMessage = {
      ...data,
      timestamp: Date.now(),
      fromPeerId: 'self',
      participantName: `${state.userNickname} (Vy)`
    };
    
    dispatch({ type: 'ADD_MESSAGE', payload: localMessage });
  };

  const sendClipboardContent = (data: { type: 'text' | 'image'; content: string }) => {
    webRTCService.current?.broadcast(data);
    
    // Add to local messages
    const message: ClipboardMessage = {
      ...data,
      timestamp: Date.now(),
      fromPeerId: 'self',
      participantName: `${state.userNickname} (Vy)`
    };
    
    dispatch({ type: 'ADD_MESSAGE', payload: message });
  };

  const copyToClipboard = async (data: { type: 'text' | 'image'; content: string }) => {
    try {
      await clipboardService.current?.copyToClipboard(data);
      showAlert(data.type === 'text' ? 'Text zkopírován do schránky!' : 'Obrázek zkopírován do schránky!', 'success');
    } catch (err) {
      showAlert('Nepodařilo se zkopírovat obsah', 'error');
    }
  };

  const showContextMenu = (event: React.MouseEvent, data: { type: 'text' | 'image'; content: string }) => {
    event.preventDefault();
    dispatch({
      type: 'SET_CONTEXT_MENU',
      payload: {
        type: data.type,
        content: data.content,
        x: event.pageX,
        y: event.pageY,
        visible: true
      }
    });
  };

  const hideContextMenu = () => {
    dispatch({ type: 'HIDE_CONTEXT_MENU' });
  };

  const contextValue: ClipShareContextType = {
    state,
    dispatch,
    createSession,
    joinSession,
    leaveSession,
    sendMessage,
    sendClipboardContent,
    copyToClipboard,
    showContextMenu,
    hideContextMenu,
    generateUserId,
    generateDefaultNickname
  };

  return (
    <ClipShareContext.Provider value={contextValue}>
      {children}
    </ClipShareContext.Provider>
  );
};

export const useClipShareContext = () => {
  const context = useContext(ClipShareContext);
  if (!context) {
    throw new Error('useClipShareContext must be used within ClipShareProvider');
  }
  return context;
};